
import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute, { NessunaSezioneAbilitata } from '../components/ProtectedRoute';
import AppShell from '../components/layout/AppShell';
import { landingPath } from '../lib/landing';
import { useAuthStore } from '../stores/auth.store';
import { usePermessiStore } from '../stores/permessi.store';
import SchedePagina from '../components/layout/SchedePagina';

const LoginPage = lazy(() => import('../pages/LoginPage'));
const OnboardingWizard = lazy(() => import('../pages/OnboardingWizard'));
const MagicLinkHandler = lazy(() => import('../pages/MagicLinkHandler'));
const SsoCallbackHandler = lazy(() => import('../pages/SsoCallbackHandler'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));

// Operator pages
const OperatorDashboard = lazy(() => import('../pages/operator/OperatorDashboard'));
const HomeDashboard = lazy(() => import('../pages/HomeDashboard'));
const PhonebookPage = lazy(() => import('../pages/phonebook/PhonebookPage'));
const BundleView = lazy(() => import('../pages/operator/BundleView'));
const FermoTecnicoQueue = lazy(() => import('../pages/operator/FermoTecnicoQueue'));
const PraticheHubPage = lazy(() => import('../pages/operator/PraticheHubPage'));
const ProfilePage = lazy(() => import('../pages/ProfilePage'));
const NotifichePage = lazy(() => import('../pages/NotifichePage'));
const GenericModulePage = lazy(() => import('../pages/operator/GenericModulePage'));
const AuthorityBodyworkPage = lazy(() => import('../pages/authority-bodywork/AuthorityBodyworkPage'));
const GenericArchivePage = lazy(() => import('../pages/admin/GenericArchivePage'));

// Admin pages
const GlobaleView = lazy(() => import('../pages/admin/GlobaleView'));
const OperatoreDetailView = lazy(() => import('../pages/admin/OperatoreDetailView'));
const PneumaticiModule = lazy(() => import('../pages/admin/PneumaticiModule'));
const SessioniView = lazy(() => import('../pages/admin/SessioniView'));
const ExportPage = lazy(() => import('../pages/admin/ExportPage'));
const LeaderboardPage = lazy(() => import('../pages/admin/LeaderboardPage'));
const InviaNotificaPage = lazy(() => import('../pages/admin/InviaNotificaPage'));
const ArchivioPratichePage = lazy(() => import('../pages/admin/ArchivioPratichePage'));
const ArchivioHubPage = lazy(() => import('../pages/admin/ArchivioHubPage'));
const ArchivioFermoTecnicoPage = lazy(() => import('../pages/admin/ArchivioFermoTecnicoPage'));

// IAM pages
const UserManagement = lazy(() => import('../pages/iam/UserManagement'));
const RolesManagement = lazy(() => import('../pages/iam/RolesManagement'));
const ModulesRegistry = lazy(() => import('../pages/iam/ModulesRegistry'));
const AuditLogPage = lazy(() => import('../pages/iam/AuditLogPage'));
const EmailLogPage = lazy(() => import('../pages/iam/EmailLogPage'));
const PermessiPage = lazy(() => import('../pages/iam/PermessiPage'));
const GadgetPage = lazy(() => import('../pages/iam/GadgetPage'));

// EFFEISBUCK pages
const EffeisbuckFeed = lazy(() => import('../pages/effeisbuck/EffeisbuckFeed'));
// News — no module gate: the front page is for every account.
const NewsPage = lazy(() => import('../pages/news/NewsPage'));
const BlocchiAdminPage = lazy(() => import('../pages/workforce/BlocchiAdminPage'));
const EffeisbuckProfile = lazy(() => import('../pages/effeisbuck/EffeisbuckProfile'));
const EffeisbuckPostPage = lazy(() => import('../pages/effeisbuck/EffeisbuckPostPage'));

// HELPDESK pages
const HelpdeskInbox = lazy(() => import('../pages/helpdesk/HelpdeskInbox'));
const HelpdeskBoard = lazy(() => import('../pages/helpdesk/HelpdeskBoard'));
const HelpdeskNewTicket = lazy(() => import('../pages/helpdesk/HelpdeskNewTicket'));
const HelpdeskTicketDetail = lazy(() => import('../pages/helpdesk/HelpdeskTicketDetail'));
const HelpdeskStats = lazy(() => import('../pages/helpdesk/HelpdeskStats'));
const HelpdeskSettings = lazy(() => import('../pages/helpdesk/HelpdeskSettings'));

// Workforce pages
const GruppiPage = lazy(() => import('../pages/workforce/GruppiPage'));
const FestivitaPage = lazy(() => import('../pages/workforce/FestivitaPage'));
const FeriePage = lazy(() => import('../pages/workforce/FeriePage'));
const PresenzePage = lazy(() => import('../pages/workforce/PresenzePage'));
const PresenzaTeamPage = lazy(() => import('../pages/workforce/PresenzaTeamPage'));
const SaldiPage = lazy(() => import('../pages/workforce/SaldiPage'));
const BancaOrePage = lazy(() => import('../pages/workforce/BancaOrePage'));
const CostoLavoroPage = lazy(() => import('../pages/workforce/CostoLavoroPage'));
const CcnlAdminPage = lazy(() => import('../pages/workforce/CcnlAdminPage'));
const MaturazioniPage = lazy(() => import('../pages/workforce/MaturazioniPage'));

// Config pages
const BrandingPage = lazy(() => import('../pages/iam/BrandingPage'));
const SmtpSettingsPage = lazy(() => import('../pages/config/SmtpSettingsPage'));
const SecuritySettingsPage = lazy(() => import('../pages/config/SecuritySettingsPage'));
const AiSettingsPage = lazy(() => import('../pages/config/AiSettingsPage'));
const SsoSettingsPage = lazy(() => import('../pages/config/SsoSettingsPage'));
const EmailTemplatesPage = lazy(() => import('../pages/config/EmailTemplatesPage'));
const RateLimitDashboard = lazy(() => import('../pages/config/RateLimitDashboard'));
const WebRTCSettingsPage = lazy(() => import('../pages/config/WebRTCSettingsPage'));
const WebhooksPage = lazy(() => import('../pages/config/WebhooksPage'));
const DataRetentionPage = lazy(() => import('../pages/config/DataRetentionPage'));
const SystemStatusPage = lazy(() => import('../pages/config/SystemStatusPage'));

function Loading() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// `SchedePagina` sta qui e non nelle singole rotte per un motivo preciso: le
// pagine raggruppate sono 22 e sarebbero 22 punti da tenere allineati a mano
// con navConfig. Qui la striscia si disegna da sé dove esiste un gruppo e
// resta invisibile altrove — non può scordarsi di una pagina né comparire su
// una che non appartiene a nessun gruppo.
function Lazy({ children }) {
  return (
    <Suspense fallback={<Loading />}>
      <SchedePagina>{children}</SchedePagina>
    </Suspense>
  );
}

// "/" is a door like any other, so it asks the same question the sign-in
// flows ask: where does this user start?
function LandingRedirect() {
  const user = useAuthStore((s) => s.user);
  const { permessi, catalogo, stato } = usePermessiStore();
  const accesso = stato === 'pronto' ? { permessi, catalogo } : null;
  const dest = landingPath(user, accesso);
  if (!dest) {
    return <NessunaSezioneAbilitata />;
  }
  return <Navigate to={dest} replace />;
}

export default function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Lazy><LoginPage /></Lazy>} />
      {/* Magic-link landing — must stay OUTSIDE /auth/ (nginx proxies /auth/*
          wholesale to auth-service). The page itself calls the verify API. */}
      <Route path="/magic-link" element={<Lazy><MagicLinkHandler /></Lazy>} />
      {/* SSO callback landing — must ALSO stay OUTSIDE /auth/, same reason as
          /magic-link above (nginx proxies /auth/* wholesale to auth-service).
          auth-service's /auth/oidc/callback redirects here with a one-time
          handoff code; this page exchanges it via its own fetch to
          /auth/oidc/complete (which correctly proxies, since that's an XHR
          not a browser navigation). */}
      <Route path="/sso-callback" element={<Lazy><SsoCallbackHandler /></Lazy>} />

      {/* Onboarding */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute authOnly>
            <Lazy><OnboardingWizard /></Lazy>
          </ProtectedRoute>
        }
      />

      {/* Authenticated routes with AppShell */}
      <Route
        element={
          <ProtectedRoute authOnly>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<LandingRedirect />} />
        {/* Home (post-login landing for Super Admin) */}
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <Lazy><HomeDashboard /></Lazy>
            </ProtectedRoute>
          }
        />
        {/* Phonebook (rubrica) — requires at least one assigned module */}
        <Route
          path="/rubrica"
          element={
            <ProtectedRoute requireAnyModule>
              <Lazy><PhonebookPage /></Lazy>
            </ProtectedRoute>
          }
        />
        {/* Operator */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Lazy><OperatorDashboard /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pratiche"
          element={
            <ProtectedRoute>
              <Lazy><PraticheHubPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pratiche/pneumatici"
          element={
            <ProtectedRoute requireModule="module_pneumatici">
              <Lazy><BundleView /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pratiche/fermo-tecnico"
          element={
            <ProtectedRoute requireModule="module_fermo_tecnico">
              <Lazy><FermoTecnicoQueue /></Lazy>
            </ProtectedRoute>
          }
        />
        {/* Authority Meccanica e Carrozzeria: modalità Lista (pagina generica) o Riepilogo */}
        <Route
          path="/pratiche/authority-bodywork"
          element={
            <ProtectedRoute requireModule="module_authority_bodywork">
              <Lazy><AuthorityBodyworkPage /></Lazy>
            </ProtectedRoute>
          }
        />
        {/* Dynamic: any module not matched above gets GenericModulePage */}
        <Route
          path="/pratiche/:moduleSlug"
          element={
            <ProtectedRoute>
              <Lazy><GenericModulePage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route path="/bundle" element={<Navigate to="/pratiche" replace />} />
        <Route path="/fermo-tecnico" element={<Navigate to="/pratiche/fermo-tecnico" replace />} />
        <Route path="/storico" element={<Navigate to="/pratiche" replace />} />
        <Route path="/profilo" element={<Lazy><ProfilePage /></Lazy>} />
        <Route path="/notifiche" element={<Lazy><NotifichePage /></Lazy>} />

        {/* News — the front page for everyone but the Super Admin */}
        <Route
          path="/news"
          element={
            <ProtectedRoute>
              <Lazy><NewsPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/effeisbuck"
          element={
            <ProtectedRoute requireModule="effeisbuck">
              <Lazy><EffeisbuckFeed /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/effeisbuck/posts/:postId"
          element={
            <ProtectedRoute requireModule="effeisbuck">
              <Lazy><EffeisbuckPostPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/effeisbuck/profilo"
          element={
            <ProtectedRoute requireModule="effeisbuck">
              <Lazy><EffeisbuckProfile /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/effeisbuck/profilo/:userId"
          element={
            <ProtectedRoute requireModule="effeisbuck">
              <Lazy><EffeisbuckProfile /></Lazy>
            </ProtectedRoute>
          }
        />

        {/* HELPDESK — module-gated */}
        <Route
          path="/helpdesk"
          element={
            <ProtectedRoute requireModule="helpdesk">
              <Lazy><HelpdeskInbox /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/helpdesk/board"
          element={
            <ProtectedRoute requireModule="helpdesk">
              <Lazy><HelpdeskBoard /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/helpdesk/new"
          element={
            <ProtectedRoute requireModule="helpdesk">
              <Lazy><HelpdeskNewTicket /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/helpdesk/tickets/:idOrKey"
          element={
            <ProtectedRoute requireModule="helpdesk">
              <Lazy><HelpdeskTicketDetail /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/helpdesk/stats"
          element={
            <ProtectedRoute requireModule="helpdesk">
              <Lazy><HelpdeskStats /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/helpdesk/settings"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']} requireModule="helpdesk">
              <Lazy><HelpdeskSettings /></Lazy>
            </ProtectedRoute>
          }
        />

        {/* Admin */}
        <Route
          path="/admin/analisi"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
              <Lazy><GlobaleView /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route path="/admin/globale" element={<Navigate to="/admin/analisi" replace />} />
        {/* Operatori mostrava lo stesso elenco di Gestione Utenti, con due
            colonne in più che ora stanno lì. L'indirizzo resta valido e porta
            dove il contenuto è andato: chi ha un segnalibro non trova un 404. */}
        <Route path="/admin/operatori" element={<Navigate to="/iam/utenti" replace />} />
        <Route
          path="/admin/operatori/:id"
          element={
            <ProtectedRoute
              codiciVista={['vista.admin.analisi', 'vista.admin.classifica', 'vista.iam.utenti']}
              allowedRoles={['ADMIN', 'SUPER_ADMIN']}
            >
              <Lazy><OperatoreDetailView /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/moduli"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
              <Lazy><PneumaticiModule /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route path="/admin/moduli/pneumatici" element={<Navigate to="/admin/moduli" replace />} />
        <Route path="/admin/export" element={<Navigate to="/admin/analisi" replace />} />
        <Route
          path="/admin/leaderboard"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
              <Lazy><LeaderboardPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/notifiche"
          element={
            <ProtectedRoute>
              <Lazy><InviaNotificaPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/archivio"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
              <Lazy><ArchivioHubPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/archivio/pneumatici"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']} requireModule="module_pneumatici">
              <Lazy><ArchivioPratichePage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/archivio/fermo-tecnico"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']} requireModule="module_fermo_tecnico">
              <Lazy><ArchivioFermoTecnicoPage /></Lazy>
            </ProtectedRoute>
          }
        />
        {/* Dynamic: any archive module not matched above gets GenericArchivePage */}
        <Route
          path="/admin/archivio/:moduleSlug"
          element={
            <ProtectedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
              <Lazy><GenericArchivePage /></Lazy>
            </ProtectedRoute>
          }
        />

        {/* Workforce — ADMIN+ for management sections, MANAGER+ for CCNL & accrual */}
        <Route
          path="/workforce/gruppi"
          element={
            /* Niente allowedRoles su questa pagina e sulle altre tre dei
               permessi d'azione: la porta è il codice `vista.*` del catalogo,
               che porta la stessa soglia di ruolo di prima e in più si può
               assegnare. Con il doppio cancello, a chi riceveva il permesso
               la pagina restava chiusa lo stesso. */
            <ProtectedRoute>
              <Lazy><GruppiPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workforce/festivita"
          element={
            <ProtectedRoute allowedRoles={['OPERATOR']}>
              <Lazy><FestivitaPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workforce/ferie"
          element={
            <ProtectedRoute allowedRoles={['OPERATOR']}>
              <Lazy><FeriePage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workforce/presenze"
          element={
            <ProtectedRoute allowedRoles={['OPERATOR']}>
              <Lazy><PresenzePage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workforce/blocchi"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <Lazy><BlocchiAdminPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workforce/presenze-team"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <Lazy><PresenzaTeamPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workforce/saldi"
          element={
            <ProtectedRoute allowedRoles={['OPERATOR']}>
              <Lazy><SaldiPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workforce/banca-ore"
          element={
            <ProtectedRoute allowedRoles={['OPERATOR']}>
              <Lazy><BancaOrePage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workforce/costo-lavoro"
          element={
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <Lazy><CostoLavoroPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workforce/ccnl"
          element={
            <ProtectedRoute>
              <Lazy><CcnlAdminPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workforce/maturazioni"
          element={
            <ProtectedRoute allowedRoles={['MANAGER']}>
              <Lazy><MaturazioniPage /></Lazy>
            </ProtectedRoute>
          }
        />

        {/* IAM */}
        <Route
          path="/iam/utenti"
          element={
            <ProtectedRoute>
              <Lazy><UserManagement /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/iam/ruoli"
          element={
            <ProtectedRoute allowedRoles={['MANAGER', 'SUPER_ADMIN']}>
              <Lazy><RolesManagement /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/iam/moduli"
          element={
            <ProtectedRoute allowedRoles={['MANAGER', 'SUPER_ADMIN']}>
              <Lazy><ModulesRegistry /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/iam/sessioni"
          element={
            <ProtectedRoute allowedRoles={['MANAGER', 'SUPER_ADMIN']}>
              <Lazy><SessioniView /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/iam/audit"
          element={
            <ProtectedRoute allowedRoles={['MANAGER', 'SUPER_ADMIN']}>
              <Lazy><AuditLogPage /></Lazy>
            </ProtectedRoute>
          }
        />
        {/* Solo SUPER_ADMIN: qui si leggono indirizzi e oggetti delle mail di
            chiunque, e un ADMIN o un MANAGER deve vedere solo la propria
            squadra. */}
        <Route
          path="/iam/email"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <Lazy><EmailLogPage /></Lazy>
            </ProtectedRoute>
          }
        />
        {/* Chi assegna permessi può darseli: il ruolo che li governa deve
            essere quello che già può tutto. */}
        <Route
          path="/iam/permessi"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <Lazy><PermessiPage /></Lazy>
            </ProtectedRoute>
          }
        />

        {/* Solo SUPER_ADMIN: taglie di tutti e indirizzi di chi è in smart
            working, per ordinare i gadget aziendali. */}
        <Route
          path="/iam/gadget"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <Lazy><GadgetPage /></Lazy>
            </ProtectedRoute>
          }
        />

        {/* Config */}
        <Route
          path="/config/branding"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <Lazy><BrandingPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/config/smtp"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <Lazy><SmtpSettingsPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/config/security"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <Lazy><SecuritySettingsPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/config/ai"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <Lazy><AiSettingsPage /></Lazy>
            </ProtectedRoute>
          }
        />

        <Route
          path="/config/sso"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <Lazy><SsoSettingsPage /></Lazy>
            </ProtectedRoute>
          }
        />

        <Route
          path="/config/email-templates"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <Lazy><EmailTemplatesPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/config/rate-limits"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <Lazy><RateLimitDashboard /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/config/webrtc"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <Lazy><WebRTCSettingsPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/config/webhooks"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <Lazy><WebhooksPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/config/data-retention"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <Lazy><DataRetentionPage /></Lazy>
            </ProtectedRoute>
          }
        />
        <Route
          path="/config/system"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <Lazy><SystemStatusPage /></Lazy>
            </ProtectedRoute>
          }
        />

        {/* Redirect old paths */}
        <Route path="/iam/branding" element={<Navigate to="/config/branding" replace />} />
        <Route path="/admin/sessioni" element={<Navigate to="/iam/sessioni" replace />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Lazy><NotFoundPage /></Lazy>} />
    </Routes>
  );
}
